import { useIslands } from '../../../hooks/useIslands';
import { doSwaggerCall } from '../../../hooks/useSwaggerClient';

export const saveClient = async (clientId, values) => {
    const {
        primaryColor,
        textColor,
        secondaryColor,
        fontFamily,
        contact_email_customer_limit,
        name,
        legal_name,
        address,
        contact_name,
        contact_phone_office,
        contact_phone_reservation,
        contact_phone_customer,
        contact_email_office,
        contact_email_reservation,
        contact_email_customer,
        client_url,
        tax_number,
        tax_island_id,
        payment_method,
        guest_fields,
        header_guest_fields,
        hotjarid,
        hotjarsv,
        gaTrackingId,
        tags,
        featured_tags,
        featured_activities,
        monthly_fee,
        defaultIsland,
        visibleIslands,
        restrictedToOneIsland,
        hideActivitiesWithoutTimeslot,
        aliceApikey,
        aliceUsername,
        alicePassword,
        aliceHotelId,
        aliceFacilityId,
        aliceServiceId,
        aliceStatusCreateId,
        aliceStatusCancelId,
        withholdCustomerMail,
        agentEmail,
        logoImage,
        is_vendor,
        is_client,
        customBookingPrefix,
        admin_notes,
        office_information,
        billing_information,
        billing_terms,
        allowed_selling,
        agreement,
        location,
        vendor_price_items,
        client_price_items,
        show_taxes_and_fees,
        vendor_limitations,
        vendor_limitation_type,
        activity_limitations,
        activity_limitation_type,
        client_limitations,
        client_limitation_type,
        chains,
        enable_golf,
        custom_agency_discount,
        custom_vendor_discount,
        // gl codes
        client_gl,
        vendor_gl,
        vendor_number,
        default_discount,
        commissionType = 'retail',
        voucherWithPricing = true,
        weeklyReport = false,
        privateApiConfig = null
    } = values;

    return doSwaggerCall('clients', clientId ? 'updateClient' : 'createClient', {
        clientId,
        newClient: {
            name,
            legal_name,
            is_vendor,
            is_client,
            address,
            contact_name,
            contact_phone: {
                office: contact_phone_office,
                reservation: contact_phone_reservation,
                customer: contact_phone_customer
            },
            contact_email: {
                office: contact_email_office,
                reservation: contact_email_reservation,
                customer: contact_email_customer
            },
            client_config: JSON.stringify({
                tags,
                withholdCustomerMail,
                agentEmail,
                defaultIsland,
                visibleIslands,
                restrictedToOneIsland,
                hideActivitiesWithoutTimeslot,
                hotjarid,
                hotjarsv,
                gaTrackingId,
                primaryColor,
                textColor,
                secondaryColor,
                fontFamily,
                contact_email_customer_limit,
                logoImage,
                customBookingPrefix: customBookingPrefix.toUpperCase(),
                agreement,
                location: location ?? {},
                commissionType,
                voucherWithPricing,
                weeklyReport
            }),
            client_url,
            tax_number,
            tax_island_id,
            payment_method,
            guest_fields,
            header_guest_fields,
            admin_notes,
            office_information,
            billing_information,
            billing_terms,
            allowed_selling,
            featured_tags,
            featured_activities,
            monthly_fee,
            notify_list: JSON.stringify(
                aliceApikey
                    ? [
                          {
                              type: 'alice',
                              apikey: aliceApikey,
                              username: aliceUsername,
                              password: alicePassword,
                              hotelId: aliceHotelId,
                              facilityId: aliceFacilityId,
                              serviceId: aliceServiceId,
                              statusCreateId: aliceStatusCreateId,
                              statusCancelId: aliceStatusCancelId,
                              states: {
                                  create: 824,
                                  request: 300,
                                  accept: 302,
                                  working: 303,
                                  decline: 305,
                                  close: 304,
                                  expire: 306
                              }
                          }
                      ]
                    : []
            ),
            vendor_price_items,
            client_price_items,
            show_taxes_and_fees,
            limitations: JSON.stringify({
                vendors: {
                    list: vendor_limitations,
                    type: vendor_limitation_type
                },
                activities: {
                    list: activity_limitations,
                    type: activity_limitation_type
                },
                clients: {
                    list: client_limitations,
                    type: client_limitation_type
                }
            }),
            enable_golf,
            chains,
            custom_agency_discount,
            custom_vendor_discount,
            // gl codes
            client_gl,
            vendor_gl,
            vendor_number,
            default_discount,
            private_api_config: privateApiConfig ? JSON.stringify(privateApiConfig) : null
        }
    });
};

export const parseClient = client => {
    const [islands] = useIslands(true);
    const {
        name = '',
        legal_name = '',
        address = '',
        contact_name = '',
        contact_phone = {
            office: '',
            reservation: '',
            customer: ''
        },
        contact_email = {
            office: '',
            reservation: '',
            customer: ''
        },
        client_url = '',
        tax_number = '',
        tax_island_id = islands.find(i => i.name === 'Oʻahu')?.id,
        payment_method = [],
        client_config = '',
        notify_list = '',
        is_vendor = false,
        is_client = true,
        guest_fields = [],
        header_guest_fields = [],
        admin_notes = '',
        allowed_selling = true,
        featured_tags = [],
        featured_activities = [],
        monthly_fee = 0,
        office_information = '',
        billing_information = '',
        billing_terms = '',
        vendor_price_items = [],
        client_price_items = [],
        show_taxes_and_fees = 'aggregated',
        limitations = {},
        enable_golf = true,
        chains = [],
        custom_agency_discount = true,
        custom_vendor_discount = true,
        // GL codes
        client_gl = {},
        vendor_gl = {},
        vendor_number = '',
        default_discount = 0,
        private_api_config = null,
        client_key = ''
    } = client ?? {};

    const clientConfigData = client_config ? JSON.parse(client_config) : {};

    // Parse private_api_config - can be string or object from API
    const privateApiConfig = private_api_config
        ? typeof private_api_config === 'string'
            ? JSON.parse(private_api_config)
            : private_api_config
        : null;

    const {
        hotjarid = '',
        hotjarsv = '',
        gaTrackingId = '',
        tags = [],
        withholdCustomerMail = true,
        agentEmail = true,
        defaultIsland: rawDefaultIsland = [],
        visibleIslands = [],
        restrictedToOneIsland = false,
        hideActivitiesWithoutTimeslot = 'default_on',
        primaryColor = '#2c4056',
        textColor = '#333333',
        secondaryColor = '#83a8d3',
        fontFamily = 'Roboto',
        contact_email_customer_limit = '',
        logoImage = '',
        customBookingPrefix = '',
        agreement = '',
        location = '',
        commissionType = 'retail',
        voucherWithPricing = true,
        weeklyReport = false
    } = clientConfigData;

    // Normalize defaultIsland - handle old format (string) and new format (array)
    const defaultIsland = Array.isArray(rawDefaultIsland)
        ? rawDefaultIsland
        : typeof rawDefaultIsland === 'string' && rawDefaultIsland.length > 0
        ? [rawDefaultIsland]
        : [];

    const {
        apikey: aliceApikey = '',
        username: aliceUsername = '',
        password: alicePassword = '',
        hotelId: aliceHotelId = '',
        facilityId: aliceFacilityId = '',
        serviceId: aliceServiceId = '',
        statusCreateId: aliceStatusCreateId = '',
        statusCancelId: aliceStatusCancelId = ''
    } = notify_list ? JSON.parse(notify_list).find(({ type }) => type === 'alice') ?? {} : {};

    return {
        is_vendor: !!is_vendor,
        is_client: !!is_client,
        primaryColor,
        textColor,
        secondaryColor,
        fontFamily,
        contact_email_customer_limit,
        name,
        legal_name: legal_name || '',
        address: address || '',
        contact_name: contact_name || '',
        contact_phone_office: contact_phone?.office,
        contact_phone_reservation: contact_phone?.reservation,
        contact_phone_customer: contact_phone?.customer,
        contact_email_office: contact_email?.office,
        contact_email_reservation: contact_email?.reservation,
        contact_email_customer: contact_email?.customer,
        client_url: client_url || '',
        tax_number: tax_number || '',
        tax_island_id,
        payment_method,
        guest_fields,
        header_guest_fields,
        hotjarid,
        hotjarsv,
        gaTrackingId,
        tags,
        featured_tags,
        featured_activities,
        monthly_fee,
        defaultIsland,
        visibleIslands,
        restrictedToOneIsland,
        hideActivitiesWithoutTimeslot,
        aliceApikey,
        aliceUsername,
        alicePassword,
        aliceHotelId,
        aliceFacilityId,
        aliceServiceId,
        aliceStatusCreateId,
        aliceStatusCancelId,
        withholdCustomerMail,
        agentEmail,
        logoImage,
        customBookingPrefix,
        admin_notes,
        office_information,
        billing_information,
        billing_terms,
        allowed_selling,
        agreement,
        location,
        vendor_price_items,
        client_price_items,
        show_taxes_and_fees,
        vendor_limitations: limitations?.vendors?.list || [],
        vendor_limitation_type: limitations?.vendors?.type || 'exclude',
        activity_limitations: limitations?.activities?.list || [],
        activity_limitation_type: limitations?.activities?.type || 'exclude',
        client_limitations: limitations?.clients?.list || [],
        client_limitation_type: limitations?.clients?.type || 'exclude',
        enable_golf,
        chains,
        custom_agency_discount,
        custom_vendor_discount,
        // gl codes
        client_gl,
        vendor_gl,
        vendor_number: vendor_number || '',
        default_discount,
        commissionType,
        voucherWithPricing,
        weeklyReport,
        privateApiConfig,
        client_key
    };
};
